import { graphql, Environment } from 'relay-runtime';
import commitMutation from 'relay-commit-mutation-promise';
import { executeCaptcha } from 'dibs-recaptcha/exports/recaptcha';
import { guestWalletUpdater } from '../wallet/guestWalletUpdater';
import { SubmitResult, createSubmitError } from '../submitPaymentMethod';

import { submitPayPalPaymentMethodVaultMutation } from './__generated__/submitPayPalPaymentMethodVaultMutation.graphql';
import { useCallback } from 'react';
import { PaymentMethod } from '../../types';

const submitPayPalPaymentMethodVaultMutation = graphql`
    mutation submitPayPalPaymentMethodVaultMutation($input: UserVaultPaymentMethodInput!) {
        userVaultPaymentMethod(input: $input) {
            user {
                wallet {
                    paymentMethods {
                        paymentMethod {
                            paymentMethodType
                            ... on CreditCard {
                                token
                                paymentProcessor
                                cardLogo
                                cardType
                                cardExpiryMonth
                                cardExpiryYear
                                formattedTextMaskedCardNumber
                            }
                            ... on PayPalAccount {
                                token
                                email
                                paymentProcessor
                            }
                            ... on ACH {
                                token
                                paymentProcessor
                                accountType
                                last4
                                accountHolderName
                                routingNumber
                            }
                            ... on ApplePay {
                                token
                                paymentProcessor
                            }
                        }
                        shippingAddresses {
                            serviceId
                        }
                    }
                }
            }
            walletPaymentMethod {
                paymentMethod {
                    ... on PayPalAccount {
                        token
                        email
                        paymentProcessor
                    }
                }
                shippingAddresses {
                    serviceId
                }
            }
        }
    }
`;

type Options = {
    userId: string;
    viewerId?: string;
    verifyPaymentMethod: boolean;
    relayEnvironment: Environment;
    mockWallet: boolean;
};

export async function submitPayPalPaymentMethod(
    payPalClient: $TSFixMe,
    { userId, viewerId, relayEnvironment, verifyPaymentMethod, mockWallet }: Options
): Promise<string | null> {
    return new Promise<string | null>((resolve, reject) => {
        payPalClient.tokenize(
            { flow: 'vault' },
            async (tokenizeError: $TSFixMe, payload: $TSFixMe) => {
                if (tokenizeError) {
                    if (tokenizeError.type === 'CUSTOMER') {
                        // popup closed before completing
                        resolve(null);
                    } else {
                        reject(
                            createSubmitError('IMPLEMENTATION', 'PayPal tokenize error', {
                                payPalError: tokenizeError,
                            })
                        );
                    }
                }

                try {
                    const captchaResults = await executeCaptcha({
                        action: 'checkoutVaultPayPal',
                        featureFlag: 'vaultCreditCardCaptcha',
                        featureFlagV3: 'vaultCreditCardCaptchaV3',
                        relayEnvironment,
                    });

                    const response = await commitMutation<submitPayPalPaymentMethodVaultMutation>(
                        relayEnvironment,
                        {
                            mutation: submitPayPalPaymentMethodVaultMutation,
                            variables: {
                                input: {
                                    userId,
                                    mockWallet,
                                    saveToWallet: !mockWallet,
                                    paymentMethodNonce: payload.nonce,
                                    verifyPaymentMethod,
                                    captchaToken: captchaResults?.captchaToken,
                                    captchaKey: captchaResults?.captchaKey,
                                },
                            },
                            updater:
                                mockWallet && viewerId
                                    ? store =>
                                          guestWalletUpdater<submitPayPalPaymentMethodVaultMutation>(
                                              store,
                                              viewerId,
                                              userId,
                                              'userVaultPaymentMethod'
                                          )
                                    : null,
                        }
                    );
                    const savedToken =
                        response?.userVaultPaymentMethod?.walletPaymentMethod?.paymentMethod?.token;

                    if (savedToken) {
                        resolve(savedToken);
                    } else {
                        reject();
                    }
                } catch (error: $TSFixMe) {
                    reject(
                        createSubmitError('VAULT', 'PayPal vault error', {
                            payPalError: error?.source?.errors?.[0],
                        })
                    );
                }
            }
        );
    });
}

export function usePayPalSubmit(): (paymentMethod: PaymentMethod) => Promise<SubmitResult> {
    return useCallback(async paymentMethod => {
        if (paymentMethod.paymentMethodToken) {
            return { paymentMethod };
        } else {
            return createSubmitError('VALIDATION', 'PayPal must be vaulted');
        }
    }, []);
}
